// Admin — Installer Growth Pilot onboarding submissions list.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ClipboardList, Loader2 } from "lucide-react";
import { formatApiError } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOURS = {
  Draft: "bg-soft text-mid-neutral",
  Submitted: "bg-blue-100 text-blue-800",
  "In review": "bg-yellow-100 text-yellow-800",
  "Information requested": "bg-orange-100 text-orange-800",
  "Ready for onboarding": "bg-mint/20 text-forest",
  Approved: "bg-lime text-forest",
  Archived: "bg-soft text-mid-neutral",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function OnboardingList() {
  const [items, setItems] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/onboarding`, { params: { ...(status ? { status } : {}), ...(q.trim() ? { q: q.trim() } : {}) } });
      setItems(r.data.items);
      setStatuses(r.data.statuses);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-onboarding" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Pilot Onboarding</h1>
        <p className="mt-2 text-mid-neutral">Installer Growth Pilot client onboarding submissions.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="onboarding-status-filter"
          className="p-2 rounded-lg bg-white border border-border-neutral text-xs font-semibold text-forest">
          <option value="">All statuses</option>
          {["Draft", ...statuses].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, business, contact…"
            data-testid="onboarding-search"
            className="p-2 rounded-lg bg-white border border-border-neutral text-xs text-forest w-72" />
        </form>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <section className="rounded-2xl bg-white border border-border-neutral overflow-hidden">
        {!items ? (
          <div className="flex items-center justify-center h-32 text-mid-neutral">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-mid-neutral text-center">No onboarding submissions found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-mid-neutral border-b border-border-neutral">
                  <th className="p-3">Reference</th>
                  <th className="p-3">Business</th>
                  <th className="p-3">Primary contact</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Services</th>
                  <th className="p-3">Areas</th>
                  <th className="p-3">Start</th>
                  <th className="p-3">Submitted</th>
                  <th className="p-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-border-neutral last:border-0 hover:bg-soft/60" data-testid={`onboarding-row-${it.id}`}>
                    <td className="p-3 font-mono text-xs font-bold text-forest">
                      <Link to={`/admin/installer-growth-pilot/onboarding/${it.id}`} className="underline" data-testid={`onboarding-open-${it.id}`}>
                        {it.reference || "Draft"}
                      </Link>
                    </td>
                    <td className="p-3 font-semibold text-forest">{it.businessName}</td>
                    <td className="p-3 text-mid-neutral">{it.contactName}<br /><span className="text-xs">{it.email}</span></td>
                    <td className="p-3">
                      <span className={"rounded-full px-2.5 py-1 text-[11px] font-bold " + (STATUS_COLOURS[it.status] || "bg-soft text-forest")}>{it.status}</span>
                    </td>
                    <td className="p-3 text-mid-neutral">{(it.services || []).join(" + ") || "—"}</td>
                    <td className="p-3 text-mid-neutral">{(it.areas || []).join(", ") || "—"}</td>
                    <td className="p-3 text-mid-neutral">{it.campaignStart || "—"}</td>
                    <td className="p-3 text-mid-neutral">{fmt(it.submittedAt)}</td>
                    <td className="p-3 text-mid-neutral">{fmt(it.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <div className="flex items-center gap-2 text-xs text-mid-neutral">
        <ClipboardList className="h-3.5 w-3.5" />
        Drafts are visible here for oversight but may still be in progress by the applicant.
      </div>
    </div>
  );
}
